import fetch from "unfetch";
import { HttpLink } from "apollo-link-http";
import { onError } from "apollo-link-error";
import { ApolloLink, execute, makePromise } from "apollo-link";
import SSO from "../sso/index";
import _config from "../config";

const API_ENDPOINT = _config.REACT_APP_API_URL;

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
};

/**
 * Get auth context for GraphQL requests
 * Cookie-based authentication: Cookies are sent automatically via credentials: 'include'
 */
export const getAuthContext = ({ headers = {} } = {}) => ({
  headers: {
    ...headers,
  },
  credentials: 'include',
});

/**
 * Authentication middleware for Apollo Link
 */
const authMiddleware = new ApolloLink((operation, forward) => {
  operation.setContext(getAuthContext);
  return forward(operation);
});

/**
 * Handles GraphQL errors including authentication failures
 */
const handleGraphQLError = ({ message, locations, path, extensions }) => {
  console.error(
    `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, Status: ${extensions?.code}`,
  );
  
  const isAuthError = extensions?.code === HTTP_STATUS.UNAUTHORIZED || 
                     extensions?.code === "UNAUTHENTICATED" || 
                     extensions?.code === "UNAUTHORIZED";
  
  if (isAuthError) {
    SSO.redirectToSignInUrl();
  }
};

/**
 * Handles network errors including authentication failures
 */
const handleNetworkError = (networkError) => {
  const errorMessage = networkError?.statusCode === HTTP_STATUS.UNAUTHORIZED
    ? "[Network error]: UNAUTHORIZED"
    : `[Network error]: ${networkError}`;
  
  console.error(errorMessage);
  
  if (networkError?.statusCode === HTTP_STATUS.UNAUTHORIZED) {
    SSO.redirectToSignInUrl();
  }
};

/**
 * Combined error handler for GraphQL and network errors
 */
const handleErrors = ({ graphQLErrors = [], networkError }) => {
  if (graphQLErrors.length > 0) {
    graphQLErrors.forEach(handleGraphQLError);
  } else if (networkError) {
    handleNetworkError(networkError);
  }
};

const errorHandlerLink = onError(handleErrors);

const composableLinks = [
  authMiddleware,
  errorHandlerLink,
  new HttpLink({
    uri: API_ENDPOINT,
    fetch,
    credentials: "include",
  }),
];

const link = ApolloLink.from(composableLinks);

const request = operation => makePromise(execute(link, operation));

// Export helpers for testing
export const helpers = {
  handleGraphQLError,
  handleNetworkError,
  handleErrors,
};

export default request;